/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useState, useEffect, useMemo, useCallback } from 'react';
import Styles from '../../css/LoanInformation.module.css';
import useAbortController from '../../common/hooks/useAbortController';
import EnvelopeWithBox from '../../components/EnvelopeWithBox';
import LoadingModal from '../../components/modals/LoadingModal';
import { useLoanContext } from '../../contexts/LoanContext';
import { useAuthContext } from '../../contexts/AuthContext';
import axiosCall from '../../services/axios';
import { getYearUnderPlan, isStrEmptyOrNull } from '../../common/UtilityFunctions';
import { Status, useBatchEditable } from '../../common/hooks/useBatchEditable';
import ButtonBorderWrapper from '../../components/ButtonBorderWrapper';
import { Button } from '@mui/material';
import ButtonBorder from '../../components/ButtonBorder';
import BtnStyles from '../../css/OneTimePaymentPage.module.css';
import { NumericFormat } from 'react-number-format';

function LoanBuydownForm() {
  const { loanNumber } = useLoanContext();
  const { token } = useAuthContext();
  const { get } = useAbortController();

  const {
    curr: loanData,
    handleChange: handleLoanChange,
    saveChangesToBackend: saveLoan,
    hasChanges: hasLoanChanges,
    status: loanStatus,
  } = useBatchEditable<LoanData>({
    endpoint: {
      path: !isStrEmptyOrNull(loanNumber) ? process.env.REACT_APP_LOAN.replace('?', loanNumber) : '',
      requestTypes: ['GET', 'POST'],
    },
  });

  const { initBuyDown, lpi, firstPaymentDate, buydownSaveYr1, buydownSaveYr2, buydownSaveYr3, buydown } = loanData;

  //Pulling data from LoanPaymentEndpoint
  const [lnptData, setLnptData] = useState({ isLoading: true, subSuspenseBal: 0 });
  const getLnptData = useCallback(async () => {
    try {
      if (!loanNumber || !token) return;
      const request = {
        url: `live/loan/${loanNumber}/payment/balance/${new Date().getFullYear().toString()}${(
          new Date().getMonth() + 1
        )
          .toString()
          .padStart(2, '0')}${new Date().getDate().toString().padStart(2, '0')}`,
      };
      const config = get();
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall(request, config, headers);
      if (!response || !response.loanNumber) throw new Error(`Invalid response for loan number ${loanNumber}`);
      setLnptData(response);
    } catch (error) {
      console.error(`LoanBuydownPage.getLnptData encountered an error: ${error.message}`);
      setLnptData((prev) => ({ ...prev, isLoading: false }));
    }
  }, [loanNumber, token, get]);
  useEffect(() => {
    getLnptData();
  }, [getLnptData]);

  const monthsBetween = useMemo(() => {
    if (!lpi || !firstPaymentDate) {
      console.error('Could not retrieve date(s) to calculate installments');
      return;
    }

    let paidToDate = new Date(lpi * 1000);
    let ptdMonth = paidToDate.getMonth() + 1;
    let ptdYear = paidToDate.getFullYear();
    let firstPayDate = new Date(firstPaymentDate * 1000);
    let fpdMonth = firstPayDate.getMonth() + 1;
    let fpdYear = firstPayDate.getFullYear();

    // convert to useRef if it needs to be outside of the useMemo
    let installments = 0;

    if (ptdYear < fpdYear || (ptdYear === fpdYear && ptdMonth < fpdMonth)) {
      installments = 0; //No installments, because the paid-to-date is behind the first payment date
      return;
    } else if (ptdYear === fpdYear && ptdMonth === fpdMonth) {
      installments = 1; //1 installment, since paid-to-date exactly matches the first payment date (borrower has paid through the first payment)
      return;
    }

    installments = 1;

    //Installments starts at 1 here, and for every month after
    while (fpdYear < ptdYear || (fpdYear === ptdYear && fpdMonth < ptdMonth)) {
      installments++;
      fpdMonth++;
      if (fpdMonth === 13) {
        fpdMonth = 1;
        fpdYear++;
      }
    }
    return installments;
  }, [lpi, firstPaymentDate]);

  const yearUnderPlan = useMemo(() => {
    return getYearUnderPlan(monthsBetween);
  }, [monthsBetween]);

  return (
    <ButtonBorderWrapper>
      <EnvelopeWithBox blockName='Loan Buydown'>
        {(loanStatus == Status.LOADING || lnptData.isLoading) && <LoadingModal open />}
        <div className={Styles.loanInfoTable}>
          <div className={Styles.leftColumn}>
            <table className={Styles.loanInfoTableSection}>
              <tbody>
                <tr>
                  <th>Orig. Subsidy Balance:</th>
                  <td>
                    <NumericFormat
                      value={initBuyDown}
                      className={Styles.narrowCellSpacing}
                      prefix={'$'}
                      decimalScale={2}
                      fixedDecimalScale
                      type='text'
                      thousandsGroupStyle='thousand'
                      thousandSeparator=','
                      disabled={false}
                      onValueChange={(values, sourceInfo) => {
                        handleLoanChange('edit', 'initBuyDown', values.floatValue);
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <th>Subsidy Balance:</th>
                  <td>{lnptData.subSuspenseBal}</td>
                </tr>
                <tr>
                  <th>Installments Paid:</th>
                  <td>{monthsBetween > 0 ? monthsBetween : 'No Payments'}</td>
                </tr>
                {buydown && (
                  <tr>
                    <th>Year Under Plan:</th>
                    <td>{yearUnderPlan}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className={Styles.rightColumn}>
            <table className={Styles.loanInfoTableSection}>
              <tbody>
                <tr>
                  <th></th>
                  <th>Amount</th>
                  <th>Monthly Amount</th>
                </tr>
                <tr>
                  <th>Year 1:</th>
                  <td>
                    <NumericFormat
                      value={buydownSaveYr1}
                      className={Styles.narrowCellSpacing}
                      prefix={'$'}
                      decimalScale={2}
                      fixedDecimalScale
                      type='text'
                      thousandsGroupStyle='thousand'
                      thousandSeparator=','
                      disabled={false}
                      onValueChange={(values, sourceInfo) => {
                        handleLoanChange('edit', 'buydownSaveYr1', values.floatValue);
                      }}
                    />
                  </td>
                  <td>
                    <NumericFormat
                      value={Math.round((buydownSaveYr1 / 12) * 100) / 100}
                      className={Styles.narrowCellSpacing}
                      prefix={'$'}
                      decimalScale={2}
                      fixedDecimalScale
                      type='text'
                      thousandsGroupStyle='thousand'
                      thousandSeparator=','
                      disabled={true}
                    />
                  </td>
                </tr>
                <tr>
                  <th>Year 2:</th>
                  <td>
                    <NumericFormat
                      value={buydownSaveYr2}
                      className={Styles.narrowCellSpacing}
                      prefix={'$'}
                      decimalScale={2}
                      fixedDecimalScale
                      type='text'
                      thousandsGroupStyle='thousand'
                      thousandSeparator=','
                      disabled={false}
                      onValueChange={(values, sourceInfo) => {
                        handleLoanChange('edit', 'buydownSaveYr2', values.floatValue);
                      }}
                    />
                  </td>

                  <td>
                    <NumericFormat
                      value={Math.round((buydownSaveYr2 / 12) * 100) / 100}
                      className={Styles.narrowCellSpacing}
                      prefix={'$'}
                      decimalScale={2}
                      fixedDecimalScale
                      type='text'
                      thousandsGroupStyle='thousand'
                      thousandSeparator=','
                      disabled={true}
                    />
                  </td>
                </tr>
                <tr>
                  <th>Year 3:</th>
                  <td>
                    <NumericFormat
                      value={buydownSaveYr3}
                      className={Styles.narrowCellSpacing}
                      prefix={'$'}
                      decimalScale={2}
                      fixedDecimalScale
                      type='text'
                      thousandsGroupStyle='thousand'
                      thousandSeparator=','
                      disabled={false}
                      onValueChange={(values, sourceInfo) => {
                        handleLoanChange('edit', 'buydownSaveYr3', values.floatValue);
                      }}
                    />
                  </td>
                  <td>
                    <NumericFormat
                      value={Math.round((buydownSaveYr3 / 12) * 100) / 100}
                      className={Styles.narrowCellSpacing}
                      prefix={'$'}
                      decimalScale={2}
                      fixedDecimalScale
                      type='text'
                      thousandsGroupStyle='thousand'
                      thousandSeparator=','
                      disabled={true}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </EnvelopeWithBox>
      <ButtonBorder>
        <Button
          disabled={!hasLoanChanges}
          variant='contained'
          type='button'
          className={BtnStyles.button}
          onClick={() => {
            handleLoanChange('cancel');
          }}
        >
          Cancel
        </Button>
        <Button
          disabled={!hasLoanChanges}
          variant='contained'
          type='submit'
          className={BtnStyles.button}
          onClick={saveLoan}
        >
          Save
        </Button>
      </ButtonBorder>
    </ButtonBorderWrapper>
  );
}

export default LoanBuydownForm;
